// Drill-down de una celda de Inteligencia de negocios (Fase 3).
// Primer nivel: de qué cuentas sale la cifra, con el peso con que entra cada una
// (las referencias a otras filas ya vienen expandidas del servidor, así que un
// TOTAL construido con referencias también se puede abrir). Segundo nivel: el
// Libro Mayor de la cuenta en el rango de esa columna.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, useMarcaEmpresa, type Celda } from '@/lib/utils/exportarTabla';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { usd, rpc } from './shared';

interface CuentaDet {
  cuenta_id: string; codigo: string; nombre: string; tipo: string;
  peso: number; monto: number; aporte: number;
}
export interface Detalle {
  reporte: string; fila: string; medida: string;
  columna: string; columna_titulo: string;
  desde: string | null; hasta: string;
  mayor: { disponible: boolean; centro: string | null; proyecto: string | null };
  total: number; cuentas: CuentaDet[];
}
interface Mayor {
  saldo_inicial: number; total_debe: number; total_haber: number;
  movimientos: { fecha: string; numero: number; glosa: string | null; debe: number; haber: number }[];
}

export function BIDetalleModal({ params, notaEscala, onCerrar }: {
  params: {
    reporte: string; filaOrden: number; colClave: string;
    desde: string; hasta: string; colsTipo: string; colsParam: string;
    centro: string | null; proyecto: string | null;
  };
  /** Si el reporte se está viendo en miles o millones, se avisa: aquí los
   *  importes van EXACTOS. Escalar un asiento individual no tendría sentido. */
  notaEscala?: string;
  onCerrar: () => void;
}) {
  const [cuenta, setCuenta] = useState<CuentaDet | null>(null);
  const marca = useMarcaEmpresa();

  const { data, isFetching, error } = useQuery<Detalle>({
    queryKey: ['bi-detalle', params],
    queryFn: () => rpc<Detalle>('cont_bi_detalle', {
      _reporte: params.reporte, _fila_orden: params.filaOrden, _col_clave: params.colClave,
      _desde: params.desde, _hasta: params.hasta,
      _cols_tipo: params.colsTipo, _cols_param: params.colsParam,
      _centro: params.centro, _proyecto: params.proyecto,
    }),
  });

  const { data: mayor, isFetching: cargandoMayor } = useQuery<Mayor>({
    queryKey: ['bi-mayor', cuenta?.cuenta_id, data?.desde, data?.hasta, data?.mayor],
    queryFn: () => rpc<Mayor>('cont_libro_mayor', {
      _cuenta_id: cuenta!.cuenta_id,
      // en filas de SALDO la celda acumula desde el inicio de la contabilidad
      _desde: data!.desde ?? '1900-01-01', _hasta: data!.hasta,
      _proyecto: data!.mayor.proyecto, _centro: data!.mayor.centro,
    }),
    enabled: !!cuenta && !!data,
  });

  const headers = ['Código', 'Cuenta', 'Peso', 'Importe', 'Aporte'];
  const filas: Celda[][] = (data?.cuentas ?? []).map((c) => [c.codigo, c.nombre, c.peso, c.monto, c.aporte]);

  return (
    <PanelIntegrado title={cuenta ? `${cuenta.codigo} · ${cuenta.nombre}` : data?.fila ?? 'Detalle'} onClose={onCerrar} wide>
      <div>
        <div className="flex items-center gap-2 pb-2">
          {cuenta && (
            <button onClick={() => setCuenta(null)} className="rounded p-1 hover:bg-muted" title="Volver">
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs text-muted-foreground">
              {data && `${data.reporte} · ${data.columna_titulo} · ${data.medida === 'saldo' ? 'saldo acumulado' : 'movimiento del período'}`}
              {data && (data.desde ? ` · ${data.desde} a ${data.hasta}` : ` · hasta ${data.hasta}`)}
            </p>
            {notaEscala && (
              <p className="text-[11px] text-amber-600">
                El reporte se está viendo en {notaEscala}; aquí los importes van exactos.
              </p>
            )}
          </div>
          {!cuenta && data && data.cuentas.length > 0 && (
            <ExportarBotones nombre={`detalle-${data.fila.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              titulo={`${data.fila} · ${data.columna_titulo}`} headers={headers} filas={filas}
              colsDerecha={[2, 3, 4]} marca={marca}
              fmtPDF={(v, c) => (c < 2 ? String(v ?? '') : c === 2 ? String(v ?? '') : usd(Number(v ?? 0)))} />
          )}
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {(isFetching || cargandoMayor) && (
            <div className="py-8 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>
          )}
          {!!error && <p className="py-6 text-center text-sm text-destructive">{getErrorMessage(error)}</p>}

          {/* Nivel 1 — cuentas que componen la celda */}
          {!cuenta && !isFetching && data && (
            data.cuentas.length === 0
              ? <p className="py-8 text-center text-sm text-muted-foreground">
                  Esta celda no tiene movimientos en el período.
                </p>
              : (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-xs text-muted-foreground">
                        <th className="py-2 pr-3 text-left">Cuenta</th>
                        <th className="px-2 py-2 text-right" title="Con qué signo y cuántas veces entra la cuenta en esta fila">Peso</th>
                        <th className="px-2 py-2 text-right">Importe</th>
                        <th className="px-2 py-2 text-right">Aporte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.cuentas.map((c) => (
                        <tr key={c.cuenta_id} className="border-b last:border-0 hover:bg-muted/50">
                          <td className="py-1.5 pr-3">
                            <button className="text-left disabled:cursor-default"
                              disabled={!data.mayor.disponible}
                              title={data.mayor.disponible ? 'Ver el Libro Mayor de esta cuenta' : 'El Libro Mayor no se puede filtrar por esta columna'}
                              onClick={() => data.mayor.disponible && setCuenta(c)}>
                              <span className="font-mono text-xs text-muted-foreground">{c.codigo}</span>{' '}
                              <span className={data.mayor.disponible ? 'underline decoration-dotted underline-offset-2' : ''}>{c.nombre}</span>
                            </button>
                          </td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${c.peso < 0 ? 'text-destructive' : ''}`}>
                            {c.peso > 0 ? '+' : ''}{c.peso}
                          </td>
                          <td className="px-2 py-1.5 text-right tabular-nums">{usd(c.monto)}</td>
                          <td className={`px-2 py-1.5 text-right tabular-nums ${c.aporte < 0 ? 'text-destructive' : ''}`}>
                            {usd(c.aporte)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t bg-muted/60 font-semibold">
                        <td className="py-2 pr-3" colSpan={3}>Total de la celda</td>
                        <td className={`px-2 py-2 text-right tabular-nums ${data.total < 0 ? 'text-destructive' : ''}`}>
                          {usd(data.total)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {!data.mayor.disponible && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Esta columna no se puede llevar al Libro Mayor: solo se filtra por centro de costo o
                      proyecto concretos, no por partner ni por las columnas «(Sin asignar)» y «(Otros)».
                    </p>
                  )}
                </>
              )
          )}

          {/* Nivel 2 — Libro Mayor de la cuenta */}
          {cuenta && !cargandoMayor && mayor && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left">Fecha</th>
                  <th className="py-2 pr-3 text-left">Asiento</th>
                  <th className="py-2 pr-3 text-left">Glosa</th>
                  <th className="px-2 py-2 text-right">Debe</th>
                  <th className="px-2 py-2 text-right">Haber</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b text-xs text-muted-foreground">
                  <td className="py-1.5 pr-3" colSpan={3}>Saldo inicial</td>
                  <td className="px-2 py-1.5 text-right tabular-nums" colSpan={2}>{usd(mayor.saldo_inicial)}</td>
                </tr>
                {mayor.movimientos.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Sin movimientos en el período.</td></tr>
                )}
                {mayor.movimientos.map((m, i) => (
                  <tr key={`${m.numero}-${i}`} className="border-b last:border-0">
                    <td className="whitespace-nowrap py-1.5 pr-3">{m.fecha}</td>
                    <td className="py-1.5 pr-3 tabular-nums">#{m.numero}</td>
                    <td className="py-1.5 pr-3">{m.glosa}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{m.debe ? usd(m.debe) : ''}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums">{m.haber ? usd(m.haber) : ''}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/60 font-semibold">
                  <td className="py-2 pr-3" colSpan={3}>Totales del período</td>
                  <td className="px-2 py-2 text-right tabular-nums">{usd(mayor.total_debe)}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{usd(mayor.total_haber)}</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </PanelIntegrado>
  );
}
